import React, { useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Outlet, useLocation } from 'react-router-dom';

import { Box, CssBaseline, Paper as MuiPaper } from '@mui/material';
import { spacing } from '@mui/system';

import GlobalStyle from '../components/GlobalStyle';
import dashboardItems from '../components/sidebar/dashboardItems';
import salespersonDashboardItems from '../components/sidebar/salespersonDashboardItems';
import adminDashboardItems from '../components/sidebar/adminDashboardItems';
import userDashboardItems from '../components/sidebar/userDashboardItems';
import partnerDashboardItems from '../components/sidebar/partnerDashboardItems';
import productionDashboardItems from '../components/sidebar/productionDashboardItems';
import { getSettingsItems, isSettingsPath } from '../components/sidebar/settingsItems';

import Sidebar from '../components/sidebar/Sidebar';
import Navbar from '../components/navbar/Navbar';
import AdminBreadcrumbs from '../components/breadcrumbs/AdminBreadcrumbs';
import { isUserRole, ROLES } from '../utils/jwt';
import { GlobalContext } from '../contexts/GlobalContext';
import { updateOptimizationMenuCounts } from '../utils/dashboard';
import InvoiceAlert from '../components/InvoiceAlert';
import DpaAgreementBanner from '../components/DpaAgreementBanner';
import useAuth from '../hooks/useAuth';

const drawerWidth = 200;
const mobileDrawerWidth = 240;

const Root = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
`;

const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up('md')} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  min-width: 0;
  min-height: 0;
`;

const Paper = styled(MuiPaper)(spacing);

const MainContent = styled(Paper)`
  flex: 1;
  min-height: 0;
  overflow: auto;
  background: ${(props) => props.theme.palette.background.default};

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    font-size: 10px;

    box-shadow: none;
  }
`;

const Dashboard = ({ children }) => {
  const router = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const role = isUserRole();
  const { user } = useAuth();
  const { optimizationListAmounts } = useContext(GlobalContext);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    setMobileOpen(false);
  }, [router.pathname]);

  const isInternal =
    role === ROLES.ADMIN || role === ROLES.SALESPERSON || role === ROLES.PRODUCTION;

  const getDashboardItems = () => {
    if (role === ROLES.ADMIN) {
      return updateOptimizationMenuCounts(adminDashboardItems, optimizationListAmounts);
    }
    if (role === ROLES.CUSTOMER) {
      if (user?.dpaStatus?.exempted) {
        return dashboardItems.map((section) => ({
          ...section,
          pages: section.pages.filter((item) => item.href !== '/client/avtal'),
        }));
      }
      return dashboardItems;
    }
    if (role === ROLES.SALESPERSON) {
      return salespersonDashboardItems;
    }
    if (role === ROLES.PRODUCTION) {
      return productionDashboardItems;
    }
    if (role === ROLES.PARTNER) {
      return partnerDashboardItems;
    }
    return userDashboardItems;
  };

  const inSettingsView = isSettingsPath(router.pathname, role);
  const sidebarItems = inSettingsView ? getSettingsItems(role) : getDashboardItems();

  return (
    <Root>
      <CssBaseline />
      <GlobalStyle />
      <Drawer>
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <Sidebar
            PaperProps={{ style: { width: mobileDrawerWidth } }}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            items={sidebarItems}
            onDrawerToggle={handleDrawerToggle}
          />
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <Sidebar PaperProps={{ style: { width: drawerWidth } }} items={sidebarItems} />
        </Box>
      </Drawer>
      <AppContent>
        <Navbar onDrawerToggle={handleDrawerToggle} />
        <InvoiceAlert />
        <DpaAgreementBanner />
        {isInternal && <AdminBreadcrumbs />}
        <MainContent>
          {children}
          <Outlet />
        </MainContent>
      </AppContent>
    </Root>
  );
};

export default Dashboard;
